import { Spinner } from '@chakra-ui/react'
import styled from '@emotion/styled'
import { Component } from 'react'
import {
  EncodingCounters,
  ProductInfoField,
  ProductionOrder,
  ProductionOrderRow,
  ProductionOrderRows,
  ProductionOrders,
  TmrProduct
} from 'stylewhere/api'
import {
  Box,
  EncodingCountersBox,
  EncodingProduct,
  FormSchemaForm,
  InfoBox,
  KeyValueRow,
  Spacer,
  Text
} from 'stylewhere/components'
import { EncodingExtensions } from 'stylewhere/extensions'
import { AppStore, FormSchema, FormSchemaData } from 'stylewhere/shared'
import { T, __ } from 'stylewhere/shared/i18n'
import { EncodingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  getEncodingProductFields,
  getInitialType,
  getInitialTypeOptions,
  isProductionOrderCompleted,
  showCountersOnEncoding,
  showToastError,
  isModalError,
  isCanEncodeWithOverQuantity,
  isCheckEncodedQuantity
} from 'stylewhere/utils'

export const DEFAULT_PRODUCTION_ORDER_ATTRIBUTES = 4

interface Props {
  operation: EncodingOperationConfig
  onSubmit: (data: FormSchemaData) => void
  onProductionOrderReceived?: (productionOrder: ProductionOrder) => void
  updateFormSchemaData?: (data: FormSchemaData) => void
  onProductionOrderClear?: () => void
  onProductClear?: () => void
  onIdentifierClear?: () => void
  data?: FormSchemaData
  product?: TmrProduct
  counters?: EncodingCounters
  resetStateCounter?: number
  disabledClear?: boolean
  disabledClearProduct?: boolean
  disableInput?: boolean
  overrideFormSchema?: FormSchema
  overrideProductInfoFields?: ProductInfoField[]
  style?: React.CSSProperties
  flex?: boolean
  productionOrderDetail?: () => void
  enableKoSounds?: boolean
}
export interface EncodingFormState {
  data?: FormSchemaData
  productionOrder?: ProductionOrder
  productionOrderRow?: ProductionOrderRow
  resetStateCounter?: number
  loading: boolean
  loadingCounterProductionOrder: boolean
  loadingProductionOrderRow: boolean
  viewProductionOrderAttribute: boolean
}

export class EncodingForm<T extends EncodingFormState> extends Component<Props, T> {
  productionOrderFields = EncodingExtensions.getProductionOrderFields(this.props.operation)
  isModal = false

  state: T = {
    data: this.props.data,
    resetStateCounter: this.props.resetStateCounter || 1,
    loading: false,
    loadingCounterProductionOrder: false,
    loadingProductionOrderRow: false,
    viewProductionOrderAttribute: false
  } as T

  componentDidMount() {
    this.isModal = isModalError(this.props.operation)

    const { data } = this.props
    if (data && data.productionOrder && data.productionOrder.id) {
      this.setState({ productionOrder: data.productionOrder })
    }
  }

  shouldComponentUpdate = (nextProps) => {
    const { data, resetStateCounter, loading, loadingCounterProductionOrder, loadingProductionOrderRow } = this.state
    if (
      !loading &&
      !loadingCounterProductionOrder &&
      !loadingProductionOrderRow &&
      nextProps.data &&
      JSON.stringify(nextProps.data) !== JSON.stringify(data)
    ) {
      const initialType = getInitialType(this.props.operation)
      if (nextProps.resetStateCounter && resetStateCounter !== nextProps.resetStateCounter) {
        if (initialType === 'order') {
          this.setState({
            data: nextProps.data,
            resetStateCounter: nextProps.resetStateCounter,
            productionOrder: nextProps.data.productionOrder,
            productionOrderRow: undefined
          })
        } else {
          this.setState({
            data: nextProps.data,
            resetStateCounter: nextProps.resetStateCounter,
            productionOrderRow: undefined
          })
        }
      } else {
        const discard = initialType === 'order' && nextProps.data.productionOrderRow && !nextProps.data.productionOrder
        if (!discard) this.setState({ data: nextProps.data })
      }
    } else if (
      !loading &&
      !loadingCounterProductionOrder &&
      !loadingProductionOrderRow &&
      nextProps.resetStateCounter &&
      resetStateCounter !== nextProps.resetStateCounter
    ) {
      const initialType = getInitialType(this.props.operation)
      if (initialType === 'order') {
        this.setState({
          resetStateCounter: nextProps.resetStateCounter,
          productionOrder: undefined,
          productionOrderRow: undefined
        })
      } else {
        this.setState({ resetStateCounter: nextProps.resetStateCounter, productionOrderRow: undefined })
      }
    } else if (loadingProductionOrderRow && nextProps.data && nextProps.data.productionOrderRow) {
      this.setState({ loadingProductionOrderRow: false })
    }

    return true
  }

  initialTypeProduct = () => {
    const { data } = this.state
    const { disableInput, overrideFormSchema, style, flex, operation } = this.props
    return (
      <FormSchemaForm
        flex={flex === false ? flex : true}
        initialValues={data}
        style={style ?? { justifyContent: 'space-between' }}
        schema={overrideFormSchema ?? EncodingExtensions.formSchema(this.props.operation, data)}
        onSubmit={this.props.onSubmit}
        submitText={__(T.misc.next)}
        disabled={disableInput}
        operation={operation}
      />
    )
  }

  clearProductionOrderInfo = () => {
    this.props.onProductionOrderClear?.()
    if (this.state.data?.productionOrder)
      this.setState({
        productionOrderRow: undefined,
        data: { productionOrder: this.state.data.productionOrder }
      })
    else {
      this.setState({ productionOrderRow: undefined })
    }
  }

  clearProductInfo = () => {
    this.props.onProductClear?.()
    if (this.state.data?.productionOrder)
      this.setState({
        productionOrderRow: undefined,
        data: { productionOrder: this.state.data.productionOrder }
      })
    else {
      this.setState({ productionOrderRow: undefined })
    }
  }

  productInfoBox = () => {
    const {
      product,
      counters,
      onIdentifierClear,
      operation,
      disabledClear,
      disabledClearProduct,
      overrideProductInfoFields,
      flex,
      productionOrderDetail
    } = this.props
    const { productionOrder, productionOrderRow, data } = this.state
    const displayProductionOrder = productionOrder ?? productionOrderRow?.productionOrder
    return (
      <EncodingProduct
        initialType={getInitialType(operation)}
        code={data?.wam}
        operationId={operation.id}
        product={
          {
            ...(product ?? productionOrderRow!.product),
            productionOrderRow: productionOrderRow?.code
          } as any
        }
        productionOrder={displayProductionOrder}
        clear={onIdentifierClear}
        productClose={disabledClearProduct ? undefined : this.clearProductInfo}
        fields={overrideProductInfoFields ?? getEncodingProductFields()}
        disabledClear={disabledClear}
        flex={flex}
        counters={counters}
        productionOrderDetail={productionOrderDetail}
      />
    )
  }

  formSchemaProductionOrder = () => {
    const { operation, overrideFormSchema, disableInput } = this.props
    return (
      <FormSchemaForm
        key="formProductionOrder"
        flex
        style={{ justifyContent: 'space-between' }}
        schema={overrideFormSchema ?? EncodingExtensions.formSchema(operation, this.state.data)}
        onSubmit={this.submitProductionOrder}
        submitText={__(T.misc.next)}
        disabled={disableInput}
        operation={operation}
      />
    )
  }

  renderLoading = () => {
    return (
      <KeyValueRow style={{ justifyContent: 'flex-start', alignItems: 'center' }}>
        <Spinner color="#454545" />
        <Text style={{ marginLeft: 10 }} center fontSize={14}>
          {__(T.misc.loading)}
        </Text>
      </KeyValueRow>
    )
  }

  managerViewProductionOrderAttribute = () => {
    this.setState({ viewProductionOrderAttribute: !this.state.viewProductionOrderAttribute })
  }

  getProductionOrderFields = () => {
    const { viewProductionOrderAttribute } = this.state
    if (viewProductionOrderAttribute) {
      return this.productionOrderFields
    }
    return this.productionOrderFields.slice(0, DEFAULT_PRODUCTION_ORDER_ATTRIBUTES)
  }

  formSchemaProductionOrderRow = () => {
    const initialType = getInitialType(this.props.operation)
    const { disableInput, operation, overrideFormSchema, counters, productionOrderDetail } = this.props
    const { productionOrder, productionOrderRow, loadingCounterProductionOrder, loadingProductionOrderRow } = this.state
    const displayProductionOrder = productionOrder ?? productionOrderRow?.productionOrder
    return (
      <Box flex>
        {displayProductionOrder && (
          <>
            <InfoBox
              title={__(T.misc.production_order)}
              subtitle={displayProductionOrder?.code}
              data={displayProductionOrder}
              fields={this.getProductionOrderFields()}
              showMore={
                this.productionOrderFields.length > DEFAULT_PRODUCTION_ORDER_ATTRIBUTES
                  ? () => this.managerViewProductionOrderAttribute()
                  : undefined
              }
              viewMore={this.state.viewProductionOrderAttribute}
              onCloseClick={this.clearProductionOrderInfo}
            />
            <Spacer height={'10'} />
            {!productionOrderRow && loadingCounterProductionOrder && (
              <>
                {this.renderLoading()}
                <Spacer height={'10'} />
              </>
            )}
            {(!productionOrderRow || loadingProductionOrderRow) &&
              counters &&
              showCountersOnEncoding(initialType, counters) &&
              !loadingCounterProductionOrder && (
                <>
                  <EncodingCountersBox
                    counters={counters}
                    productionOrder={displayProductionOrder}
                    productionOrderDetail={productionOrderDetail}
                  />
                  <Spacer height={'10'} />
                </>
              )}
          </>
        )}
        {!!productionOrderRow && !loadingProductionOrderRow && this.productInfoBox()}
        {loadingProductionOrderRow && this.renderLoading()}
        {!productionOrderRow &&
          !loadingProductionOrderRow &&
          !isProductionOrderCompleted(
            counters,
            isCheckEncodedQuantity(operation),
            isCanEncodeWithOverQuantity(operation)
          ) && (
            <FormSchemaForm
              key="formProductionOrderRow"
              flex
              style={{ justifyContent: 'space-between' }}
              schema={overrideFormSchema ?? EncodingExtensions.formSchema(operation, this.state.data)}
              onSubmit={this.submitProductionOrderRow}
              // initialValues={this.state.data}
              submitText={__(T.misc.next)}
              disabled={disableInput}
              operation={operation}
            />
          )}
      </Box>
    )
  }

  submitProductionOrder = async (data: { productionOrder: string }) => {
    const { onProductionOrderReceived, updateFormSchemaData } = this.props
    try {
      if (data.productionOrder) {
        const initialTypeOptions = getInitialTypeOptions(this.props.operation)
        const additionaParameters = initialTypeOptions?.productionOrdersParams || {}
        const res = await ProductionOrders.search<ProductionOrder>({
          equalCodes: data.productionOrder,
          ...additionaParameters
        })
        if (res.content.length === 0) {
          throw new Error('Production order not found')
        }
        const productionOrder = res.content[0]
        if (this.checkWorkerOnProductionOrder() === 'yes') {
          const workstationPlaceCode = AppStore.getWorkstationPlaceCode()
          if (
            productionOrder.worker.code !== workstationPlaceCode &&
            !AppStore.hasAnyCapabilities('System.User.allPlaceAccess')
          ) {
            showToastError(__(T.error.WRONG_PLACE), __(T.error.error), this.isModal)
            return
          }
        } else if (this.checkWorkerOnProductionOrder() === 'userPlaces') {
          const user = AppStore.loggedUser
          const userPlaces = user?.userPlaces
          if (userPlaces && userPlaces.length > 0) {
            const workerCode = productionOrder.worker.code
            const userPlaceCodes = userPlaces.map((place) => place.code)
            const hasAccess = userPlaceCodes.includes(workerCode)

            if (!hasAccess && !AppStore.hasAnyCapabilities('System.User.allPlaceAccess')) {
              showToastError(__(T.error.WRONG_PLACE), __(T.error.error), this.isModal)
              return
            }
          }
        }
        this.setState({ data, productionOrder })
        if (onProductionOrderReceived) {
          this.setState({ loadingCounterProductionOrder: true })
          await onProductionOrderReceived(productionOrder)
          this.setState({ loadingCounterProductionOrder: false })
        }
        if (updateFormSchemaData) {
          updateFormSchemaData({ productionOrder })
        }
      }
    } catch (error) {
      this.onSubmitError(error)
      showToastError(
        __(T.error.couldn_find_production_order),
        __(T.error.error),
        this.isModal,
        undefined,
        undefined,
        this.props.enableKoSounds
      )
    }
  }

  submitProductionOrderRow = async (data: { productionOrder: string; productionOrderRow: string }) => {
    if (!data || (!data.productionOrderRow && !data.productionOrder)) {
      return
    }
    this.setState(
      {
        loadingProductionOrderRow: true,
        data: {
          ...this.state.data,
          productionOrderRow: data.productionOrderRow
        }
      },
      this.processProductionOrderRow
    )
  }

  processProductionOrderRow = async () => {
    const { productionOrder, data } = this.state
    const { operation, onSubmit } = this.props
    try {
      let productionOrderRow
      const mode = this.getOrderSelectionMode()
      const initialType = getInitialType(operation)
      const productionOrderCodes = initialType !== 'orderRow' ? [data.productionOrder.code ?? data.productionOrder] : []
      const param =
        mode === 'byOrderAndSku'
          ? {
              equalProductionOrderCodes: productionOrderCodes,
              equalProductCodes: data.productionOrderRow
            }
          : {
              equalProductionOrderCodes: productionOrderCodes,
              equalCodes: data.productionOrderRow
            }
      const res = await ProductionOrderRows.search<ProductionOrderRow>(param)
      if (res && res.content && res.content.length > 0) {
        productionOrderRow = res.content[0]
        this.setState({ productionOrderRow })

        if (initialType !== 'orderRow') onSubmit({ productionOrder, productionOrderRow })
        else onSubmit({ productionOrderRow })
      } else {
        this.setState({ loadingProductionOrderRow: false })
        throw new Error('Production order row not found')
      }
    } catch (error) {
      this.setState({ loadingProductionOrderRow: false })
      this.onSubmitError(error)
      showToastError(
        __(T.error.couldn_find_production_order_row),
        __(T.error.error),
        this.isModal,
        undefined,
        undefined,
        this.props.enableKoSounds
      )
    }
  }

  onSubmitError = (error: any) => {}

  getOrderSelectionMode = () => {
    const { operation } = this.props
    return operation.options?.productionOrderRowSelectionMode
      ? operation.options.productionOrderRowSelectionMode
      : 'byOrderAndRowCode'
  }

  checkWorkerOnProductionOrder = () => {
    const { operation } = this.props
    return operation?.options?.checkWorkerOnProductionOrder
  }

  render() {
    const initialType = getInitialType(this.props.operation)
    const { data, loading } = this.state
    const { product } = this.props

    if (loading) {
      return (
        <LoadingContainer>
          <Spinner color="#454545" />
          <Text style={{ marginTop: 15 }} center fontSize={14}>
            {__(T.misc.loading)}
          </Text>
        </LoadingContainer>
      )
    }

    if (initialType === 'product' || initialType === 'wam' || initialType === 'formSchema') {
      if (product) return this.productInfoBox()
      return this.initialTypeProduct()
    }

    // Initial type is production order
    if (initialType === 'order') {
      if (!data || !data.productionOrder) return this.formSchemaProductionOrder()
      else if (data && data.productionOrder) {
        return this.formSchemaProductionOrderRow()
      }
    }

    // Initial type is order row
    if (initialType === 'orderRow') return this.formSchemaProductionOrderRow()

    return null
  }
}

const LoadingContainer = styled(Box)`
  background: #ffffff;
  box-sizing: border-box;
  border-radius: 15px;
  padding: 20px;
  justify-content: center;
  align-items: center;
  flex: 1;
`
